import { fetcher } from 'utils/axios';

// =============================================================================
// תגיות איש קשר — server-nodejs (ContactTagDefinitions + ContactTagAssignments).
// אוצר נפרד לחלוטין מתגיות השיחה (SessionConversationTag / OwnerCompany.ChatUserLabels):
// תגית שיחה נצמדת לסשן ונמחקת איתו; תגית איש קשר נצמדת לאיש הקשר עצמו ומופנית לפי id,
// כך ששינוי שם מתעדכן בכל מקום. תשובות במעטפת web3 { apiVer, success, data, correlationId }.
// =============================================================================

type Web3Envelope<T> = {
  apiVer?: string;
  success: boolean;
  data: T;
  correlationId?: string;
  error?: { code?: string; message?: string };
};

/** הגדרת תגית באוצר החברה */
export type ContactTagDef = {
  id: number;
  name: string;
  color: string | null;
  isActive: boolean;
  sortOrder: number;
  contactsCount?: number;
};

/** תגית כפי שהיא מוצמדת לאיש קשר (תת-קבוצה של ההגדרה) */
export type ContactTagRef = {
  id: number;
  name: string;
  color: string | null;
};

/** זיהוי איש קשר — טלפון או מזהה ChatUsers.Id */
export type ContactTarget = { phone: string; contactId?: never } | { contactId: number; phone?: never };

export type ContactTagsBulkResult = {
  action: 'add' | 'remove';
  total: number;
  succeeded: number;
  failed: number;
  errors: Array<{ target: string; message: string }>;
};

async function post<T>(path: string, data: unknown): Promise<T | null> {
  try {
    const res = (await fetcher('post', [path, { data }], 'servernode')) as Web3Envelope<T> | null;
    if (!res?.success) {
      if (res?.error?.message) console.warn(`[contact-tags] ${path}: ${res.error.message}`);
      return null;
    }
    return res.data;
  } catch (error) {
    console.warn(`[contact-tags] ${path} error: `, error);
    return null;
  }
}

// ─── אוצר ────────────────────────────────────────────────────────────────────

/** POST /api/contactTags/list — כל ההגדרות של החברה, כולל לא-פעילות */
export async function listContactTags(): Promise<ContactTagDef[] | null> {
  const data = await post<{ tags: ContactTagDef[] }>('/api/contactTags/list', {});
  return data && Array.isArray(data.tags) ? data.tags : null;
}

export async function createContactTag(input: { name: string; color?: string | null }): Promise<ContactTagDef | null> {
  const data = await post<{ tag: ContactTagDef }>('/api/contactTags/create', input);
  return data?.tag ?? null;
}

export async function updateContactTag(input: {
  id: number;
  name?: string;
  color?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}): Promise<ContactTagDef | null> {
  const data = await post<{ tag: ContactTagDef }>('/api/contactTags/update', input);
  return data?.tag ?? null;
}

export async function deleteContactTag(id: number): Promise<{ deleted: boolean; assignmentsRemoved: number } | null> {
  return post<{ deleted: boolean; assignmentsRemoved: number }>('/api/contactTags/delete', { id });
}

// ─── הצמדה ───────────────────────────────────────────────────────────────────

type AssignResult = { contactId: number; phone: string; tags: ContactTagRef[] };

/** מחזיר את רשימת התגיות המלאה של איש הקשר אחרי השינוי (השרת הוא מקור האמת) */
export async function assignContactTags(
  target: ContactTarget,
  tags: { tagIds?: number[]; tagNames?: string[] },
): Promise<ContactTagRef[] | null> {
  const data = await post<AssignResult>('/api/contactTags/assign', { ...target, ...tags });
  return data && Array.isArray(data.tags) ? data.tags : null;
}

export async function unassignContactTags(
  target: ContactTarget,
  tags: { tagIds?: number[]; tagNames?: string[] },
): Promise<ContactTagRef[] | null> {
  const data = await post<AssignResult>('/api/contactTags/unassign', { ...target, ...tags });
  return data && Array.isArray(data.tags) ? data.tags : null;
}

/** פעולה גורפת — עד 500 אנשי קשר בקריאה */
export async function bulkContactTags(input: {
  action: 'add' | 'remove';
  phones?: string[];
  contactIds?: number[];
  tagIds: number[];
}): Promise<ContactTagsBulkResult | null> {
  return post<ContactTagsBulkResult>('/api/contactTags/bulk', input);
}

// ─── מנהל לקוח ───────────────────────────────────────────────────────────────

/** מנהל הלקוח הנפתר — כשאין בחירה מפורשת השרת מחזיר את הסופר אדמין עם isDefault=true */
export type CustomerManagerRef = { udid: string; fullName: string; isDefault: boolean };

/** POST /api/services/updateContact — managerUdid=null מחזיר לברירת המחדל (סופר אדמין) */
export async function setContactCustomerManager(phone: string, managerUdid: string | null): Promise<boolean> {
  const data = await post<unknown>('/api/services/updateContact', { phone, customerManagerUdid: managerUdid });
  return data !== null;
}

export async function bulkSetCustomerManager(input: {
  phones?: string[];
  contactIds?: number[];
  managerUdid: string | null;
}): Promise<ContactTagsBulkResult | null> {
  return post<ContactTagsBulkResult>('/api/services/bulkSetCustomerManager', input);
}
